import { defineComponent, onMounted, reactive, toRefs, watch } from "vue";
import mapboxgl from 'mapbox-gl';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import 'mapbox-gl/dist/mapbox-gl.css';

import LayerOptionsComponent from "@/ui/components/maps/common/components/LayerOptionsComponent.vue";
import { useGeneralStore } from "@/store/modules/general";
import { MapComponentProps } from "./interfaces/map-component-props.interface";
import { LayerGroup } from "../common/classes/LayerGroup";
import { delelePolygon, drawPolygon, polygonControl } from "../common/classes/Polygon";
import { MapPolygon } from "../common/interfaces/map-polygon";
import { MapMarker } from "../common/interfaces/map-marker";
import { MapCoordinate } from "../common/interfaces/map-coordinate";
import { addMarkerOnMap, getDefaultMarker } from "../common/classes/Marker";
import { MapLine } from "../common/interfaces/map-line";
import { deleteLine, drawLineOnMap } from "../common/classes/Line";
import { drawLineString, lineStringControl } from "../common/classes/LineString";
import { MapCheckpoint } from "../common/interfaces/map-checkpoint";
import { addCheckpointsOnMap, deleteCheckpoints } from "../common/classes/Checkpoint";
import { MapMarkerType } from "@/helpers/types";

mapboxgl.accessToken = 'pk.eyJ1IjoiY3Nlcmdpb2RhdmlkIiwiYSI6ImNsOG05MTFwNDAzbnAzb3J3NGRvcWNyNzcifQ.v3ffo6eZsVGtVYjwO5uFCA';

export default defineComponent({
    name: 'MapComponent',
    components: {
        LayerOptionsComponent
    },
    props: {
        lt: { type: Number },
        lg: { type: Number },
        zoom: { type: Number },

        show_default_marker: { type: Boolean, default: true },
        show_marker_on_click: { type: Boolean, default: true },
        show_extreme_markers: { type: Boolean, default: true },

        show_lines: { type: Boolean, default: false },
        lines: { type: Array as () => MapLine[] },

        show_line_string: { type: Boolean, default: false },
        type_selected: { type: String },

        show_checkpoints: { type: Boolean, default: false },
        checkpoints: { type: Array as () => MapCheckpoint[] },

        show_coordinates: { type: Boolean, default: false },
        coordinates: { type: Array as () => MapCoordinate[] },

        show_coord_transactions: { type: Boolean, default: false },
        coord_transactions: { type: Array as () => MapCoordinate[] },

        show_coord_counters: { type: Boolean, default: false },
        coord_counters: { type: Array as () => MapCoordinate[] },

        show_polygon_control: { type: Boolean, default: false },
        polygon: { type: Object as () => MapPolygon },
        enable_multiple_polygon: { type: Boolean, default: false },
        polygons: { type: Array as () => MapPolygon[] },
    },
    emits: [
        'select-coordinate',
        'draw-linestring',
        'save-linestring',
        'draw-polygon-outline',
    ],
    setup(props: MapComponentProps, { emit }) {
        const store = useGeneralStore();

        const config = reactive({
            center_lt: props.lt ?? store.latitude,
            center_lg: props.lg ?? store.longitude,
            zoom: props.zoom ?? 12,
            style: 'mapbox://styles/mapbox/streets-v12',
        });

        const configLineString = reactive({
            line_string_drawer: lineStringControl(),
            show_save_control: props.show_line_string ?? false,
        });

        const configPolygon = reactive({
            show_polygon_control: props.show_polygon_control,
        });

        let map = {} as mapboxgl.Map;
        let mapMarkers = [] as MapMarker[];

        const loadMap = () => {
            map = new mapboxgl.Map({
                container: "map-container",
                style: config.style,
                center: [config.center_lg, config.center_lt],
                zoom: config.zoom
            });

            map.addControl(new LayerGroup() as never);
            map.addControl(new mapboxgl.NavigationControl());
            map.addControl(new mapboxgl.FullscreenControl());
            map.on('load', () => { map.resize() });

            if (props.show_default_marker) {
                const marker = getDefaultMarker(config.center_lt, config.center_lg);
                drawMarkerOnMap(marker);
            }

            if (props.show_marker_on_click) {
                drawMarkerOnClick();
            }

            if (props.show_lines) {
                map.on('style.load', () => {
                    const isMarkerEnable = props.show_extreme_markers;
                    const isCheckpointEnable = props.show_checkpoints;

                    drawLines(isMarkerEnable, isCheckpointEnable);
                });
            }

            if (props.enable_multiple_polygon) {
                map.on('style.load', () => {
                    drawPolygonsOnMap();
                });
            }

            if (props.show_coord_counters) {
                map.on('style.load', () => {
                    props.coord_counters?.map(x => {
                        drawMarkerOnMap(x);
                    });
                });
            }

            if (props.show_coord_transactions) {
                map.on('style.load', () => {
                    props.coord_transactions?.map(x => {
                        drawMarkerOnMap(x);
                    });
                });
            }

            if (props.show_coordinates) {
                map.on('style.load', () => {
                    props.coordinates?.map(x => {
                        drawMarkerOnMap(x);
                    });
                });
            }

            if (configPolygon.show_polygon_control) {
                enablePolygonControl();
            }
        }

        // Mapa
        const centerMap = (lat?: number, lg?: number, zoom?: number) => {
            const latitude = lat ?? config.center_lt;
            const longitude = lg ?? config.center_lg;
            const zoomMap = zoom ?? config.zoom;

            map.setCenter({
                lat: latitude,
                lng: longitude
            });

            map.setZoom(zoomMap);
        }

        // Marcadores
        const drawMarkerOnMap = (coord: MapCoordinate) => {
            const marker = addMarkerOnMap(map, coord);
            mapMarkers.push(marker);
        }

        const drawMarkerOnClick = () => {
            map.on('click', (e) => {
                const mapCoordinate: MapCoordinate = {
                    name: 'onClickMarker',
                    coordinate: [e.lngLat.lng, e.lngLat.lat],
                    isPrimaryColor: true
                }

                if (mapMarkers.length > 0) {
                    deleteAllMarkersOnMap();
                }

                drawMarkerOnMap(mapCoordinate);
                emit('select-coordinate', e.lngLat.lat, e.lngLat.lng);
            });
        }

        const deleteAllMarkersOnMap = () => {
            if (mapMarkers.length > 0) {
                for (const mapmarker of mapMarkers) {
                    mapmarker.marker.remove();
                }
                mapMarkers = [] as MapMarker[];
            }
        }

        const deleteTransactionMarkersOnMap = () => {
            const transactionMarkers = mapMarkers.filter(x => x.type === MapMarkerType.TRANSACTION);
            if (transactionMarkers.length > 0) {
                for (const mapmarker of transactionMarkers) {
                    mapmarker.marker.remove();
                }
            }
        }

        const deleteCounterMarkersOnMap = () => {
            const counterMarkers = mapMarkers.filter(x => x.type === MapMarkerType.COUNTER);
            if (counterMarkers.length > 0) {
                for (const mapMarker of counterMarkers) {
                    mapMarker.marker.remove();
                }
            }
        }

        const drawExtremeMarker = (line: MapLine, startCoord: MapCoordinate, endCoord: MapCoordinate) => {
            deleteAllMarkersOnMap();

            const orientation = (line.identification == 'outbound') ? 'ida' : 'retorno';

            startCoord.name = 'Punto inicial de ' + orientation;
            startCoord.isPrimaryColor = true;
            drawMarkerOnMap(startCoord);

            endCoord.name = 'Punto final de ' + orientation;
            endCoord.isPrimaryColor = false;
            drawMarkerOnMap(endCoord);
        }

        // Líneas (rutas)
        const drawLines = (draw_markers?: boolean, draw_checkpoints?: boolean, lines?: MapLine[]) => {
            const linesToDraw = lines ?? props.lines;
            if (linesToDraw && linesToDraw.length > 0) {
                linesToDraw.map(line => {
                    deleteLine(map, line.identification);
                    drawLineOnMap(map, line, line.color);

                    if (draw_markers) {
                        const startCoord = line.coordinates[0];
                        const endCoord = line.coordinates[line.coordinates.length - 1];
                        drawExtremeMarker(line, startCoord, endCoord);
                    }

                    if (draw_checkpoints) {
                        deleteCheckpoints(map, line.identification);
                        addCheckpointsOnMap(map, line.coordinates, line.identification, line.color);
                    }
                });
            }
        }

        // Control LineString
        const enableLineStringControl = () => {
            map.addControl(configLineString.line_string_drawer);

            map.on('draw.create', (plot) => {
                emit('draw-linestring', plot.features[0].geometry?.coordinates);
            });

            map.on('draw.update', (plot) => {
                emit('draw-linestring', plot.features[0].geometry?.coordinates);
            });

            configLineString.show_save_control = true;
        }

        const disableLineStringControl = () => {
            map.removeControl(configLineString.line_string_drawer);
            configLineString.show_save_control = false;
        }

        const saveLineString = () => {
            emit('save-linestring', true);
        }

        // Polígonos
        const enablePolygonControl = () => {
            map.addControl(polygonControl());

            map.on('draw.create', (plot) => {
                emit('draw-polygon-outline', plot.features[0].geometry);
            });

            map.on('draw.update', (plot) => {
                emit('draw-polygon-outline', plot.features[0].geometry);
            });

            if (props.enable_multiple_polygon && props.polygons && props.polygons?.length > 0) {
                map.on('style.load', () => {
                    props.polygons?.map(x => {
                        drawPolygon(map, x);
                    });
                });
            }

            if (!props.enable_multiple_polygon && props.polygon && props.polygon.coordinates?.length > 0) {
                map.on('style.load', () => {
                    if (props.polygon) {
                        drawPolygon(map, props.polygon);
                    }
                });
            }
        }

        const drawPolygonsOnMap = (polygons?: MapPolygon[]) => {
            const polygonsToDraw = polygons ?? props.polygons;
            if (polygonsToDraw && polygonsToDraw.length > 0) {
                polygonsToDraw?.map(polygon => {
                    delelePolygon(map, polygon.identification);
                });

                polygonsToDraw?.map(polygon => {
                    drawPolygon(map, polygon);
                });
            }
        }

        // Estilos del mapa
        const updateStyleMap = (style: string) => {
            config.style = style;
            loadMap();
        }

        onMounted(() => {
            loadMap();
        })

        watch([
            () => props.lg,
            () => props.lt,
            () => props.zoom,
            () => props.show_line_string,
            () => props.lines,
            () => props.polygons,
            () => props.enable_multiple_polygon,
            () => props.show_coord_transactions,
            () => props.show_coord_counters
        ],
            (
                [newLg, newLt, newZoom, newShowLineString, newLines, newPolygons, newEnablePolygon, newShowCoordTransaction, newShowCoordCounter],
                [oldLg, oldLt, oldZoom, oldShowLineString, oldLines, oldPolygons, oldEnablePolygon, oldShowCoordTransaction, oldShowCoordCounter]
            ) => {
                if (newLt && newLg && newZoom) {
                    centerMap(newLt, newLg, newZoom);

                    if (props.show_default_marker) {
                        deleteAllMarkersOnMap();
                        const marker = getDefaultMarker(newLt, newLg);
                        drawMarkerOnMap(marker);
                    }
                }

                if (newLt != oldLt) {
                    centerMap(newLt, props.lg, props.zoom);
                }

                if (newLg != oldLg) {
                    centerMap(props.lt, newLg, props.zoom);
                }

                if (newShowLineString != oldShowLineString) {
                    if (newShowLineString) {
                        enableLineStringControl();
                    } else {
                        disableLineStringControl();
                    }

                    if (newLines && oldLines && oldLines.length > newLines.length) {
                        const line = oldLines.find(obj2 => !newLines.some(obj1 => obj1.identification === obj2.identification));
                        deleteAllMarkersOnMap();
                        deleteLine(map, line!.identification);
                        return;
                    }

                    if (props.lines && props.lines.length > 0) {
                        const line = props.lines.find((x) => x.identification == props.type_selected);
                        if (line) {
                            deleteLine(map, line.identification);
                            drawLineString(configLineString.line_string_drawer, line);
                            return;
                        }
                    }
                }

                if (newLines) {
                    const isMarkerEnable = props.show_extreme_markers;
                    const isCheckpointEnable = props.show_checkpoints;
                    drawLines(isMarkerEnable, isCheckpointEnable, newLines);
                }

                if (newShowCoordTransaction != oldShowCoordTransaction) {
                    if (!newShowCoordTransaction) {
                        deleteTransactionMarkersOnMap();
                        return;
                    }

                    props.coord_transactions?.map(x => {
                        drawMarkerOnMap(x);
                    });
                }

                if (newShowCoordCounter != oldShowCoordCounter) {
                    if (!newShowCoordCounter) {
                        deleteCounterMarkersOnMap();
                        return;
                    }

                    props.coord_counters?.map(x => {
                        drawMarkerOnMap(x);
                    });
                }

                if (newEnablePolygon != oldEnablePolygon) {
                    if (!newEnablePolygon) {
                        props.polygons?.map(x => {
                            delelePolygon(map, x.identification);
                        });

                        return;
                    }

                    drawPolygonsOnMap();
                }


                if (newPolygons) {
                    if (newEnablePolygon) {
                        drawPolygonsOnMap();
                    }
                }
            });



        return {
            ...toRefs(config),
            ...toRefs(configLineString),
            updateStyleMap,
            saveLineString
        }
    }
});